const HERO_IMAGE =
  'https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=1600&q=80';

/** Featured guide: triathlon running shoes, wetsuits, and race nutrition */
export const TRIATHLON_GEAR_NUTRITION_POST = {
  id: 'featured-triathlon-gear-nutrition',
  slug: 'triathlon-running-shoes-wetsuits-nutrition-guide',
  title: 'Triathlon Gear Guide: Running Shoes, Wetsuits & Race Nutrition',
  excerpt:
    'Value-focused running shoes for off-the-bike legs, beginner wetsuit picks, and what to eat before and during sprint to Olympic races—plus what to test in training first. Not medical advice.',
  publishedAt: '2026-07-23T11:50:00.000Z',
  coverImageUrl: HERO_IMAGE,
  authorName: 'Daily Sports Life',
  clubName: 'Gear Desk',
  blogCategory: 'exercise',
  featured: true,
  component: 'WellnessGuideBlogArticle',
  theme: 'exercise',
  relatedPostsTitle: 'Complete your race prep',
  relatedPosts: [
    {
      slug: 'triathlon-race-day-checklist',
      title: 'Triathlon Race Day Checklist: Week-Before to Finish Line',
      blurb: 'pack, transition, and execute on race morning.',
    },
    {
      slug: 'best-triathlon-bikes-value-guide-2026',
      title: 'Best Triathlon Bikes for Value in 2026',
      blurb: 'the bike leg between swim and run.',
    },
    {
      slug: 'triathlon-sprint-training-plan-one-month-before-race',
      title: 'Sprint Triathlon Training Plan (750 m · 13 mi · 5K)',
      blurb: '4-week plan to use this gear on race day.',
    },
  ],
  sections: [
    {
      type: 'stats',
      ariaLabel: 'Guide overview',
      items: [
        { value: '3 topics', label: 'Shoes · wetsuits · fuel' },
        { value: 'Sprint–Olympic', label: 'Primary focus' },
        { value: 'Train it', label: 'Never new on race day' },
        { value: 'Value picks', label: 'Quality per dollar' },
      ],
    },
    {
      type: 'panel',
      title: 'Golden rules before you buy anything',
      list: [
        'Nothing new on race day—shoes, wetsuit, gels, and breakfast must be tested in training.',
        'Triathlon run shoes should be easy to slip on wet feet; elastic laces are worth every penny.',
        'Use a triathlon wetsuit, not a surf wetsuit—surf suits restrict shoulders and are not legal at many races.',
        'Nutrition is personal: what works for your training partner may wreck your stomach.',
        'Ohio spring and fall races often need wetsuits; summer pool-only tris may not allow them—check race rules.',
      ],
      chips: ['Sprint & Olympic', 'Beginner friendly', 'Not medical advice', 'July 2026'],
    },
    {
      type: 'panel',
      title: 'Running shoes — what triathletes need',
      paragraphs: [
        'Off-the-bike running is different: feet are wet, calves are tight, and T2 seconds matter. Look for a shoe that drains well, goes on without socks (for short course), and has enough cushion for how far you are racing.',
        'Elastic laces (Lock Laces or Xtenex) save 15–30 seconds in transition and keep tension even when feet swell. Pre-powder the heel with body glide or baby powder if you run sockless.',
      ],
    },
    {
      type: 'table',
      title: 'Best running shoes by race distance (value picks)',
      intro:
        'Approximate U.S. MSRP. Break in race shoes with 20–30 easy miles before race day.',
      headers: ['Shoe', 'Approx. price', 'Best for', 'Why triathletes like it'],
      rows: [
        [
          'ASICS Noosa Tri 16',
          '~$140',
          'Sprint & Olympic race day',
          'Tri-specific: sockless-friendly heel, grippy outsole, quick on/off. Top pick for dedicated short-course race flats.',
        ],
        [
          'Hoka Mach 6',
          '~$140',
          'Training + first race shoe',
          'Forgiving cushion for tired legs; durable for high mileage; doubles as everyday trainer and race shoe on a budget.',
        ],
        [
          'ASICS Novablast 4',
          '~$140',
          'Longer run legs (10K+ off bike)',
          'More cushioning than Noosa; better for athletes who want comfort over pure speed after a hard bike.',
        ],
        [
          'Saucony Endorphin Pro 4',
          '~$230',
          'Olympic / 70.3 PR chase',
          'Carbon plate pop without feeling unstable; drains reasonably well for a super shoe.',
        ],
        [
          'Nike Vaporfly 3',
          '~$260',
          'Short-course speed seekers',
          'Maximum propulsion for 5K off the bike; shorter lifespan—reserve for races and key workouts.',
        ],
        [
          'Your current trainers',
          '$0 extra',
          'First sprint ever',
          'Totally fine for a beginner super-sprint if they are broken in. Add elastic laces and race belt.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Running shoe quick tips',
      list: [
        'Sprint/Olympic: sockless is common—test 2–3 brick runs without socks first.',
        '70.3 and longer: many athletes prefer thin socks; test in long bricks.',
        'Wide feet: Noosa and some race flats run narrow—try half size up or Novablast/Mach.',
        'Do not race in brand-new shoes—blisters at mile 2 are preventable.',
        'Keep race shoes in T2 bag only—do not train in them daily unless using a crossover like Mach 6.',
      ],
    },
    {
      type: 'panel',
      title: 'Wetsuits — when you need one & what to buy',
      paragraphs: [
        'Wetsuits are legal when water temperature is at or below USAT guidelines (typically 78°F / 26°C for age-group—confirm your race). They add buoyancy and warmth, which helps newer swimmers.',
        'Fit beats brand: a $300 suit that fits like a glove outperforms a $700 suit that gaps at the neck and restricts shoulders. Sleeveless suits suit warmer water and athletes with strong shoulders; full-sleeve adds warmth and buoyancy.',
      ],
    },
    {
      type: 'table',
      title: 'Best wetsuits for beginners (value & quality)',
      intro:
        'Entry tri wetsuits typically $200–$450. Rent one for your first cold-water race if unsure.',
      headers: ['Wetsuit', 'Approx. price', 'Best for', 'Notes'],
      rows: [
        [
          'Synergy Volution (full sleeve)',
          '~$250–$320',
          'Best overall value',
          'Wide size range; balanced buoyancy and flexibility; popular first-race pick in 2025–2026 reviews.',
        ],
        [
          'Orca Athlex Float',
          '~$300–$380',
          'Swimmers who want extra buoyancy',
          'Float panels help legs stay higher; good for athletes who fight sinking hips.',
        ],
        [
          'TYR Hurricane Cat 1',
          '~$275–$350',
          'Fast transitions',
          'Quick-release ankle cuffs; full-sleeve and sleeveless options; USAT legal.',
        ],
        [
          'Roka Maverick Comp II',
          '~$350–$450',
          'Shoulder mobility priority',
          'Flexible upper-body panels; step-up from entry suits if you swim 3× per week.',
        ],
        [
          'Zoot Wave 1',
          '~$250–$300',
          'Budget full-sleeve',
          'Often on sale; solid entry option when sized correctly at a local tri shop.',
        ],
        [
          'Rental / borrow',
          '~$50–$100 race weekend',
          'First cold-water race',
          'Smartest option before buying—learn what fit feels like in open water.',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Wetsuit fit checklist',
      orderedList: [
        'Neck: snug but not choking—1–2 finger gap when standing; no gapping when you look up.',
        'Shoulders: full stroke overhead without pulling at armpits or back.',
        'Torso: no excess bunching at lower back; suit should feel like a second skin.',
        'Wrists and ankles: cuffs flush to skin to limit water entry.',
        'Practice peeling: stand, pull zipper down, step on suit at ankle, push legs out—do this twice in training.',
      ],
      callout:
        'Never use a surf wetsuit for triathlon—the rubber is stiff in the shoulders and transition will be miserable.',
    },
    {
      type: 'panel',
      title: 'Race nutrition — before, during, and after',
      paragraphs: [
        'Fuel what you train with. Gut issues are the most common avoidable race-day problem. Start experimenting 4–6 weeks out during brick workouts.',
        'This is general sports nutrition information, not medical or dietary advice. Consult a doctor or sports dietitian if you have diabetes, food allergies, or digestive conditions.',
      ],
    },
    {
      type: 'table',
      title: 'Nutrition by race distance',
      intro:
        'Adjust for heat, body size, and intensity. Weigh yourself before/after long bricks—losing more than 2–3% body weight suggests under-drinking.',
      headers: ['Timing', 'Sprint (≤90 min)', 'Olympic (~2–3 hrs)', 'What to use'],
      rows: [
        [
          'Night before',
          'Normal dinner + extra water',
          'Carb-focused dinner; avoid heavy fat and new foods',
          'Rice, pasta, potatoes, familiar protein',
        ],
        [
          'Race morning (2–3 hrs out)',
          'Light breakfast 300–500 cal',
          '400–600 cal; top off glycogen',
          'Oatmeal, toast + banana, bagel + peanut butter',
        ],
        [
          '60 min pre-start',
          'Optional small snack',
          'Half banana or gel if hungry',
          'Only if practiced in training',
        ],
        [
          'During swim',
          'Nothing',
          'Nothing',
          'Mouthful of water OK; no gels in wetsuit pocket for sprint',
        ],
        [
          'On bike',
          'Water only (or sports drink)',
          '1 bottle/hr + 1 gel or 30–60 g carbs/hr',
          'Skratch, Gatorade, Maurten, Honeystinger—pick one brand',
        ],
        [
          'On run',
          'Water at aid stations',
          'Gel or chews every 30–40 min if Olympic',
          'SaltStick FastChews or electrolyte tabs in heat',
        ],
        [
          'Post-race',
          'Water + snack within 30 min',
          'Carbs + protein within 60 min',
          'Chocolate milk, recovery shake, or real food',
        ],
      ],
    },
    {
      type: 'panel',
      title: 'Reliable race-day fuel products (train with these first)',
      list: [
        'Gels: Maurten Gel 100, Huma Gel, GU Roctane—simple carbs, easy to carry in tri suit.',
        'Chews: Clif Bloks, Skratch Chews—good if you dislike gel texture.',
        'Electrolytes: SaltStick FastChews, Nuun tablets—especially for hot Ohio summer races.',
        'Sports drink: Skratch Sport Hydration, Gatorade Endurance—use one concentration you have tested.',
        'Caffeine: optional 30–45 min into bike for Olympic—only if tolerated in training bricks.',
      ],
    },
    {
      type: 'panel',
      title: 'Nutrition mistakes to avoid',
      list: [
        'Trying a new gel, bar, or sports drink on race morning.',
        'Skipping breakfast because of nerves—blood sugar crash on the bike hurts more.',
        'Over-eating the night before—GI distress at 6 a.m. swim start.',
        'Drinking only water in heat for 2+ hours—hyponatremia and cramping risk.',
        'Copying a pro’s 400-calorie/hour plan when you trained at 150 cal/hour.',
      ],
    },
  ],
  referenceLinks: [
    {
      label: 'USA Triathlon — wetsuit and equipment rules',
      url: 'https://www.usatriathlon.org/resources/multisport-101',
    },
    {
      label: 'ASICS — Noosa Tri lineup',
      url: 'https://www.asics.com/us/en-us/running-shoes-noosa-tri',
    },
    {
      label: 'Synergy — triathlon wetsuits',
      url: 'https://www.synergywetsuits.com/',
    },
    {
      label: 'TrainingPeaks — race-day nutrition basics',
      url: 'https://www.trainingpeaks.com/blog/',
    },
  ],
};
